import { Injectable } from '@angular/core';
import { BackendApplicationService } from '../backend/backend-application.service';
import { Observable, map, of, catchError, defer, finalize, switchMap, from, firstValueFrom } from 'rxjs';
import { LoadingMaskService } from '../loading-mask.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { AltinnApplication } from '../../models/altinn-application/altinn-application';
import { ApplicationStatus } from '../../models/altinn-application/application-status-enum';
import { BackendAltinn3SubmissionService } from '../backend/backend-altinn3-submission.service';
import { AltinnSubmissionProgressHubService, IAltinnFinalizeOutcome, CONNECTION_LOST_MESSAGE } from './altinn-attachment-upload-hub.service';


export interface IFormSubmissionResult {
  success: boolean;
  arCode: string;
  instanceGuid: string;
  unauthorizedInAltinn: boolean;
  connectionLost: boolean;
}

export class UnautorizedInAltinnError extends Error {

}

// We lost contact before learning the outcome, so the submission may still have completed
// in Altinn. Preparing again would create a second Altinn instance, so the user must not be
// offered a plain retry.
export class SubmissionConnectionLostError extends Error {

}

@Injectable({
  providedIn: 'root'
})
export class AltinnApplicationService {

  constructor(
    private readonly backendApplicationService: BackendApplicationService,
    private readonly backendAltinn3SubmissionService: BackendAltinn3SubmissionService,
    private readonly loadingMaskService: LoadingMaskService,
    private readonly oauthService: OAuthService,
    private readonly altinnSubmissionProgressHubService: AltinnSubmissionProgressHubService,
  ) {
  }

  public sendAltinn3Form(application: AltinnApplication): Observable<IFormSubmissionResult> {
    this.loadingMaskService.showLoadingMask(null);
    this.loadingMaskService.setManualOverride(true);
    this.altinnSubmissionProgressHubService.onConnectionInterruptedChange(interrupted =>
      this.loadingMaskService.updateMessage(interrupted
        ? 'Gjenoppretter forbindelsen til serveren ...'
        : 'Fortsetter sending til Altinn ...'));

    // Deferred so a missing pid claim surfaces through catchError below instead of throwing
    // synchronously out of this method.
    return defer(() => this.backendAltinn3SubmissionService.prepareSubmission(
      application.applicationID,
      application.applicationProcessID,
      this.requireBirthNo(),
    )).pipe(
      switchMap(prepareResult => {
        if (!prepareResult.success) {
          throw new UnautorizedInAltinnError(prepareResult.errorMessage);
        }

        const uploads$ = prepareResult.attachmentsToUpload.length > 0
          ? from(this.uploadAllAttachmentsAsync(application, prepareResult.altinnSubmissionId, prepareResult.instanceId, prepareResult.attachmentsToUpload))
          : of(undefined);

        this.altinnSubmissionProgressHubService.onWaitingForAntiVirus(prepareResult.altinnSubmissionId, () =>
          this.loadingMaskService.updateMessage('Vedleggene blir skannet for virus i Altinn før søknaden kan sendes. Vennligst vent.'));

        return uploads$.pipe(
          switchMap(() => from(this.startAndAwaitFinalizeAsync(application, prepareResult.altinnSubmissionId, prepareResult.instanceId))),
          map(finalizeResult => {
            if (!finalizeResult.success) {
              if (finalizeResult.connectionLost) {
                throw new SubmissionConnectionLostError(finalizeResult.errorMessage ?? CONNECTION_LOST_MESSAGE);
              }
              throw new UnautorizedInAltinnError(finalizeResult.errorMessage ?? 'Sending av søknad feilet');
            }
            return {
              success: true,
              arCode: finalizeResult.instanceId ?? '',
              // instanceId is "{partyId}/{instanceGuid}"; reconcile only knows the short ar code, so no guid there.
              instanceGuid: finalizeResult.instanceId?.split('/')[1] ?? '',
              unauthorizedInAltinn: false,
              connectionLost: false,
            } satisfies IFormSubmissionResult;
          }),
        );
      }),
      catchError((err) => of({
        success: false,
        arCode: '',
        instanceGuid: '',
        unauthorizedInAltinn: err instanceof UnautorizedInAltinnError,
        connectionLost: err instanceof SubmissionConnectionLostError,
      } satisfies IFormSubmissionResult)),
      finalize(() => {
        this.loadingMaskService.setManualOverride(false);
        this.loadingMaskService.turnOffLoadingMask();
        this.altinnSubmissionProgressHubService.disconnectAsync();
      })
    );
  }

  private async startAndAwaitFinalizeAsync(
    application: AltinnApplication,
    altinnSubmissionId: string,
    altinnInstanceId: string,
  ): Promise<IAltinnFinalizeOutcome> {
    await this.altinnSubmissionProgressHubService.connectForSubmissionAsync(altinnSubmissionId);

    const completion = this.altinnSubmissionProgressHubService.waitForFinalizeAsync(
      altinnSubmissionId,
      () => this.reconcileFinalizeOutcomeAsync(application),
    );

    await new Promise<void>((resolve, reject) => {
      this.backendAltinn3SubmissionService.startFinalize(application.applicationID, application.applicationProcessID, altinnSubmissionId, altinnInstanceId).subscribe({
        next: () => resolve(),
        error: (err) => reject(err),
      });
    });

    const result = await completion;

    this.archiveHubOutcome(application, 'finalize', { altinnSubmissionId, altinnInstanceId }, result, !result.success);

    return result;
  }

  // Called when the finalize waiter has been reconnected but silent for a couple of minutes -
  // a dropped socket does not stop the backend from finishing the submission, so before giving
  // up we check the application's real status instead of assuming the finalize never happened.
  private async reconcileFinalizeOutcomeAsync(application: AltinnApplication): Promise<IAltinnFinalizeOutcome> {
    try {
      const current = await firstValueFrom(this.backendApplicationService.getApplication(application.applicationID));
      if (current.status === ApplicationStatus.Queued || current.status === ApplicationStatus.Sent) {
        return { success: true, instanceId: current.arCode, errorMessage: null, connectionLost: false };
      }
    } catch {
      // fall through to the connection-lost outcome below - we couldn't confirm either way
    }

    return {
      success: false,
      instanceId: null,
      errorMessage: CONNECTION_LOST_MESSAGE,
      connectionLost: true,
    };
  }

  private async uploadAllAttachmentsAsync(
    application: AltinnApplication,
    altinnSubmissionId: string,
    altinnInstanceId: string,
    attachmentIds: string[],
  ): Promise<void> {
    await this.altinnSubmissionProgressHubService.connectForSubmissionAsync(altinnSubmissionId);

    for (const [index, attachmentId] of attachmentIds.entries()) {
      this.loadingMaskService.updateMessage(`Sender vedlegg ${index + 1} av ${attachmentIds.length} til Altinn`);
      const completion = this.altinnSubmissionProgressHubService.waitForUploadAsync(attachmentId);
      try {
        await firstValueFrom(this.backendAltinn3SubmissionService.uploadAttachment(application.applicationID, application.applicationProcessID, altinnSubmissionId, attachmentId, altinnInstanceId));
        const outcome = await completion;
        if (!outcome.success) {
          this.archiveHubOutcome(application, 'attachment-upload', { altinnSubmissionId, altinnInstanceId, attachmentId }, outcome, true);
          throw outcome.connectionLost
            ? new SubmissionConnectionLostError('Mistet kontakten med serveren under opplasting av vedlegg')
            : new Error('Attachment upload failed');
        }
      } catch (e) {
        void completion.catch(() => {});
        throw e;
      }
    }
  }

  // Attachment and finalize outcomes arrive over SignalR rather than as HTTP responses, so
  // AltinnRequestInterceptor cannot see them. Archive them explicitly instead. Fire-and-forget:
  // archiving is telemetry and must never abort a submission.
  private archiveHubOutcome(
    application: AltinnApplication,
    step: 'finalize' | 'attachment-upload',
    request: unknown,
    response: unknown,
    isError: boolean,
  ) {
    this.backendApplicationService.logRequest(
      application.applicationID,
      application.applicationProcessID,
      JSON.stringify(request),
      JSON.stringify(response),
      0,
      `hub://altinnSubmissionProgress/${step}`,
      'HUB',
      isError,
    ).subscribe({ error: () => { } });
  }

  private requireBirthNo(): string {
    const birthNo = this.oauthService.getIdentityClaims()?.['pid'] ?? null;
    if (birthNo === null) {
      throw new UnautorizedInAltinnError('Fant ikke fødselsnummer for innlogget bruker');
    }
    return birthNo;
  }
}
